import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Feather } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      const next = new URLSearchParams(loc.search).get("next");
      nav(next ? `/${next}` : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <span className="w-8 h-8 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] flex items-center justify-center"><Feather size={16} /></span>
          <span className="font-display text-xl">Humanly</span>
        </Link>
        <div className="surface p-8">
          <h1 className="font-display text-3xl mb-2">Welcome back</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">Sign in to keep humanizing.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <input
                data-testid="login-email"
                type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-[hsl(var(--border))] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/30 focus:border-[hsl(var(--primary))]"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label-eyebrow">Password</label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs link-underline text-[hsl(var(--muted-foreground))]">
                  Forgot?
                </Link>
              </div>
              <input
                data-testid="login-password"
                type="password" required minLength={6}
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-[hsl(var(--border))] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/30 focus:border-[hsl(var(--primary))]"
              />
            </div>
            <button
              data-testid="login-submit"
              type="submit" disabled={loading}
              className="btn-primary w-full disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <div className="mt-6 text-sm text-center text-[hsl(var(--muted-foreground))]">
            New here? <Link to="/signup" className="link-underline text-[hsl(var(--primary))] font-semibold">Create an account</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
